import React, { useEffect, useMemo, useState } from 'react'
import { Container, Row, Col, Form } from 'reactstrap'

import useAsyncService from '../../hooks/useAsyncService'
import {
    GetDeviceUpdateAuditParams,
    DeviceUpdateAuditItem,
    getDeviceUpdateAudit
} from '../../services/endpoints/devices'

import ComponentBox from '../../components/ComponentBox/ComponentBox'
import ICSFleetPicker from '../../components/ICSFleetPicker/ICSFleetPicker'
import { useForm } from 'react-hook-form'
import ICSButton from '../../components/ICS/Button/ICSButton'
import { useTimezone } from '../../hooks/useTimezone'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { Column, DataGrid, LoadPanel } from 'devextreme-react/data-grid'
import HeaderBar from '../../components/HeaderBar/HeaderBar'

export interface _DataSearchValues {
    fleetPicker: {
        organizations: number[]
        fleets: number[]
    }
    os: string
    client: string
    schip: string
    zchip: string
    showOnlyMismatches: boolean
}

export interface _DeviceUpdatesSearchBoxProps {
    onSearch: (data: _DataSearchValues) => void
}

type MatchState = 'match' | 'pending' | 'mismatch'

type MatchCell = {
    value: string
    state: MatchState
    tooltip: string
}

type DeviceUpdatesAuditGridRow = {
    id: number
    status: MatchState
    statusTooltip: string
    vehicleName: string
    deviceName: string
    osCell: MatchCell
    clientCell: MatchCell
    zchipCell: MatchCell
    schipCell: MatchCell
    scriptCell: MatchCell
    lastScriptName: string
    lastScriptTimestamp: string | null
    lastIupName: string
    lastIupTimestamp: string | null
    remediationText: string
}

const MATCH_TEXT_CLASS: Record<MatchState, string> = {
    match: 'text-success',
    pending: 'text-warning',
    mismatch: 'text-danger'
}

const MATCH_ICON: Record<MatchState, string> = {
    match: '●',
    pending: '●',
    mismatch: '●'
}

const defaultSearchValues: _DataSearchValues = {
    fleetPicker: {
        organizations: [],
        fleets: []
    },
    os: '',
    client: '',
    schip: '',
    zchip: '',
    showOnlyMismatches: false
}

const normalizeExpectedVersion = (value: string): string => value.trim()

const buildVersionMatchCell = (
    actualValue: string | null | undefined,
    expectedValue: string,
    hasPending: boolean
): MatchCell => {
    const normalizedExpectedValue = normalizeExpectedVersion(expectedValue)
    const normalizedActualValue = actualValue?.trim() ?? ''
    const hasActualValue = normalizedActualValue !== ''

    return buildMatchCell(
        hasActualValue ? normalizedActualValue : 'null',
        hasActualValue && (
            normalizedExpectedValue === ''
            || normalizedActualValue === normalizedExpectedValue
        ),
        hasPending,
        normalizedExpectedValue === '' ? 'a populated value' : normalizedExpectedValue
    )
}

const buildMatchCell = (
    value: string,
    isMatch: boolean,
    hasPending: boolean,
    expectedValue: string
): MatchCell => {
    if (isMatch) {
        return {
            value,
            state: 'match',
            tooltip: 'Matches expected value'
        }
    }

    if (hasPending) {
        return {
            value,
            state: 'pending',
            tooltip: 'Update pending'
        }
    }

    return {
        value,
        state: 'mismatch',
        tooltip: `Mismatch (Expected ${expectedValue})`
    }
}

const renderStatusCell = ({ data }: { data: DeviceUpdatesAuditGridRow }) => (
    <span
        className={MATCH_TEXT_CLASS[data.status]}
        title={data.statusTooltip}
        style={{ fontSize: '1rem', lineHeight: 1 }}
    >
        {MATCH_ICON[data.status]}
    </span>
)

const renderMatchCell = (cell: MatchCell) => (
    <span className={MATCH_TEXT_CLASS[cell.state]} title={cell.tooltip}>
        {cell.value}
    </span>
)

const renderTimestampedValue = (value: string, timestamp: string | null) => {
    const tooltip = timestamp ? `${value}\n${timestamp}` : value

    return (
        <div className="py-1" title={tooltip}>
            <div>{value}</div>
            {timestamp && <div className="small text-muted">{timestamp}</div>}
        </div>
    )
}

const renderRemediationCell = ({ data }: { data: DeviceUpdatesAuditGridRow }) => {
    if (data.status === 'match') {
        return null
    }

    if (data.status === 'pending') {
        return (
            <span title="Update in progress">
                {data.remediationText || 'Pending job'}
            </span>
        )
    }

    return (
        <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled
            title="Fix automation is coming soon"
        >
            Fix (coming soon)
        </button>
    )
}

const DeviceUpdatesSearchBox: React.FC<_DeviceUpdatesSearchBoxProps> = ({ onSearch }) => {
    const {
        handleSubmit,
        control,
        register,
        formState: { isSubmitting, isValid }
    } = useForm<_DataSearchValues>({
        mode: 'onChange',
        defaultValues: defaultSearchValues
    })

    const searchData = (data: _DataSearchValues) => {
        onSearch(data)
    }

    return (
        <Form onSubmit={handleSubmit(searchData)}>
            <ComponentBox
                isCustom={true}
                cardClass="groups-results-container"
                cardBodyId="groupsCardBody"
                title="Search Criteria"
                accordion={true}
            >
                <Row>
                    <Col xs={12} md={6}>
                        <ICSFleetPicker<_DataSearchValues, 'fleetPicker'>
                            name="fleetPicker"
                            control={control}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="border rounded bg-white p-3 h-100">
                            <h4 className="mb-3">Criteria</h4>
                            <Row>
                                <Col xs={12} sm={6}>
                                    <label className="form-label" htmlFor="device-updates-os">
                                        OS
                                    </label>
                                    <input
                                        id="device-updates-os"
                                        className="form-control"
                                        placeholder="Exact OS version"
                                        {...register('os')}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <label className="form-label" htmlFor="device-updates-client">
                                        Client
                                    </label>
                                    <input
                                        id="device-updates-client"
                                        className="form-control"
                                        placeholder="Exact Client version"
                                        {...register('client')}
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xs={12} sm={6}>
                                    <label className="form-label" htmlFor="device-updates-zchip">
                                        ZChip
                                    </label>
                                    <input
                                        id="device-updates-zchip"
                                        className="form-control"
                                        placeholder="Exact ZChip version"
                                        {...register('zchip')}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <label className="form-label" htmlFor="device-updates-schip">
                                        SChip
                                    </label>
                                    <input
                                        id="device-updates-schip"
                                        className="form-control"
                                        placeholder="Exact SChip version"
                                        {...register('schip')}
                                    />
                                </Col>
                            </Row>
                            <div className="form-check mt-3">
                                <input
                                    id="device-updates-show-only-mismatches"
                                    className="form-check-input"
                                    type="checkbox"
                                    {...register('showOnlyMismatches')}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="device-updates-show-only-mismatches"
                                >
                                    Show only mismatched devices
                                </label>
                            </div>
                            <p className="small text-muted mt-3 mb-0">
                                Empty version criteria allow any populated value. Missing or null
                                versions are treated as mismatches.
                            </p>
                        </div>
                    </Col>
                </Row>
                <Row className="mt-3 justify-content-end">
                    <Col className="col-sm-auto">
                        <ICSButton
                            icon="search"
                            text="Search"
                            type="success"
                            onClick={handleSubmit(searchData)}
                            disabled={isSubmitting || !isValid}
                        />
                    </Col>
                </Row>
            </ComponentBox>
        </Form>
    )
}

export const DeviceUpdatesAuditResults = ({ data: searchValues }: { data: _DataSearchValues }) => {
  const { formatLocalTimeFromUtc } = useTimezone()
    const requestArgs = useMemo(() => ({
        organizations: searchValues.fleetPicker.organizations,
        fleets: searchValues.fleetPicker.fleets
    }), [searchValues.fleetPicker.fleets, searchValues.fleetPicker.organizations])

    const {
        serviceLoading,
        serviceError,
        serviceValue: devices,
        setServiceArgs
    } = useAsyncService<GetDeviceUpdateAuditParams, DeviceUpdateAuditItem[]>({
    serviceCall: getDeviceUpdateAudit,
        initialArgs: requestArgs
  })

    useEffect(() => {
        setServiceArgs(requestArgs)
    }, [requestArgs, setServiceArgs])

    const rows = useMemo(() => {
    if (!devices) return []

        const mappedRows = devices.map((device): DeviceUpdatesAuditGridRow => {
            const pendingScriptJob = device.pendingFileSendJobs.find((job) => job.type === 'SCRIPT')
            const pendingSoftwareJob = device.pendingFileSendJobs.find((job) => job.type === 'SOFTWARE_UPDATE')

            const osCell = buildVersionMatchCell(
                device.versions.os?.ver,
                searchValues.os,
                Boolean(pendingSoftwareJob)
            )
            const clientCell = buildVersionMatchCell(
                device.versions.client?.ver,
                searchValues.client,
                Boolean(pendingSoftwareJob)
            )
            const zchipCell = buildVersionMatchCell(
                device.versions.zchip?.ver,
                searchValues.zchip,
                Boolean(pendingSoftwareJob)
            )
            const schipCell = buildVersionMatchCell(
                device.versions.schip?.ver,
                searchValues.schip,
                Boolean(pendingSoftwareJob)
            )

            const expectedScriptName = device.script.lastSent?.downloadFile.name ?? 'null'
            const scriptMatches = device.script.lastSent
                ? device.script.lastSent.matchesCurrent
                : device.script.current === null
            const scriptCell = buildMatchCell(
                device.script.current?.name ?? 'null',
                scriptMatches,
                Boolean(pendingScriptJob),
                expectedScriptName
            )

            const cells = [osCell, clientCell, zchipCell, schipCell, scriptCell]
            const hasFailures = cells.some((cell) => cell.state === 'mismatch')
            const hasPending = cells.some((cell) => cell.state === 'pending')
            const status: MatchState = hasFailures
                ? 'mismatch'
                : hasPending
                    ? 'pending'
                    : 'match'

            const remediationNames = new Set<string>()
            if (scriptCell.state === 'pending' && pendingScriptJob) {
                remediationNames.add(pendingScriptJob.downloadFile.name)
            }
            if (
                [osCell, clientCell, zchipCell, schipCell].some((cell) => cell.state === 'pending')
                && pendingSoftwareJob
            ) {
                remediationNames.add(pendingSoftwareJob.downloadFile.name)
            }

            return {
                id: device.device.id,
                status,
                statusTooltip: status === 'match'
                    ? 'Matches expected values'
                    : status === 'pending'
                        ? 'Update in progress'
                        : 'Mismatch detected',
                vehicleName: device.vehicle.name,
                deviceName: device.device.name,
                osCell,
                clientCell,
                zchipCell,
                schipCell,
                scriptCell,
                lastScriptName: device.script.lastSent?.downloadFile.name ?? 'null',
                lastScriptTimestamp: device.script.lastSent?.download.dateCreated
                    ? formatLocalTimeFromUtc(device.script.lastSent.download.dateCreated)
                    : null,
                lastIupName: device.softwareUpdate.lastSent?.downloadFile.name ?? 'null',
                lastIupTimestamp: device.softwareUpdate.lastSent?.download.dateCreated
                    ? formatLocalTimeFromUtc(device.softwareUpdate.lastSent.download.dateCreated)
                    : null,
                remediationText: Array.from(remediationNames).join(', ')
            }
        })

        return searchValues.showOnlyMismatches
            ? mappedRows.filter((row) => row.status !== 'match')
            : mappedRows
    }, [
        searchValues.client,
        searchValues.os,
        searchValues.schip,
        searchValues.showOnlyMismatches,
        searchValues.zchip,
        devices,
        formatLocalTimeFromUtc
    ])

  if (serviceLoading) {
    return <LoadIndicator />
  } else if (serviceError) {
    return (
      <ICSErrorCard
        title="An error occured"
        message={serviceError}
        code="error"
        fields={[]}
      />
    )
  }

  return (
        <div className="dataset-display" id="datasetGroup">
            <ComponentBox isCustom={true} title="Results" accordion={false}>
                <div className="pt-2 bg-white">
                    <DataGrid
                        id="gridContainer"
                        dataSource={rows}
                        keyExpr="id"
                        showRowLines={true}
                        showBorders={true}
                        rowAlternationEnabled={true}
                        columnAutoWidth={false}
                        allowColumnResizing={true}
                        noDataText="No devices matched the current filters."
                        paging={{
                            enabled: false
                        }}
                        scrolling={{
                            mode: 'standard',
                            showScrollbar: 'always'
                        }}
                    >
                        <LoadPanel enabled />
                        <Column
                            caption="Status"
                            width={70}
                            alignment="center"
                            allowSorting={false}
                            cellRender={renderStatusCell}
                        />
                        <Column
                            dataField="vehicleName"
                            caption="Vehicle"
                            minWidth={160}
                            allowSorting={false}
                        />
                        <Column
                            dataField="deviceName"
                            caption="Device"
                            minWidth={180}
                            allowSorting={false}
                        />
                        <Column
                            caption="OS"
                            minWidth={100}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderMatchCell(data.osCell)}
                        />
                        <Column
                            caption="Client"
                            minWidth={100}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderMatchCell(data.clientCell)}
                        />
                        <Column
                            caption="ZChip"
                            minWidth={100}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderMatchCell(data.zchipCell)}
                        />
                        <Column
                            caption="SChip"
                            minWidth={100}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderMatchCell(data.schipCell)}
                        />
                        <Column
                            caption="Script"
                            minWidth={170}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderMatchCell(data.scriptCell)}
                        />
                        <Column
                            caption="Last Script"
                            minWidth={180}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderTimestampedValue(data.lastScriptName, data.lastScriptTimestamp)}
                        />
                        <Column
                            caption="Last IUP"
                            minWidth={180}
                            allowSorting={false}
                            cellRender={({ data }: { data: DeviceUpdatesAuditGridRow }) => renderTimestampedValue(data.lastIupName, data.lastIupTimestamp)}
                        />
                        <Column
                            caption="Remediation"
                            minWidth={180}
                            allowSorting={false}
                            cellRender={renderRemediationCell}
                        />
                    </DataGrid>
                    <div
                        className="small d-flex justify-content-end align-items-center flex-wrap mt-3 px-1"
                        style={{ columnGap: '24px', rowGap: '8px' }}
                    >
                        <span className="d-inline-flex align-items-center text-muted">
                            <span className="text-success me-2" aria-hidden="true">●</span>
                            Match
                        </span>
                        <span className="d-inline-flex align-items-center text-muted">
                            <span className="text-warning me-2" aria-hidden="true">●</span>
                            Update in progress
                        </span>
                        <span className="d-inline-flex align-items-center text-muted">
                            <span className="text-danger me-2" aria-hidden="true">●</span>
                            Mismatch
                        </span>
                    </div>
                </div>
            </ComponentBox>
        </div>
  )
}

export const DeviceUpdatesPage: React.FC = () => {
    const [searchValues, setSearchValues] = useState<_DataSearchValues | null>(null)

    return (
        <Container fluid={true} className="wivi-container">
            <Row>
                <Col xs={12}>
                    <HeaderBar
                        origin="Devices"
                        routes={[
                            {
                                title: 'Fleet Manager'
                            }
                        ]}
                        help={{
                            title: 'Fleet Manager',
                            text: 'Review firmware and script matches for devices in the selected organizations and fleets. This first phase provides a best-effort view of expected versions, pending updates, and devices that need attention.'
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <DeviceUpdatesSearchBox onSearch={setSearchValues} />
                </Col>
            </Row>
            {searchValues && (
                <Row className="mt-3">
                    <Col xs={12}>
                        <DeviceUpdatesAuditResults data={searchValues} />
                    </Col>
                </Row>
            )}
        </Container>
    )
}

export default DeviceUpdatesPage